import React, { useCallback, useMemo, useState } from 'react';
import { DatabaseRevisionSchemaWithoutFilters } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { BiasTask } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { BiasTaskId } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { BiasTaskIds } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { BiasTaskWithoutId } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { TransformUniqueValuesByColumnItem } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { Switch } from 'apps/sigma2-ui/src/components/ui/Switch';
import { MetricDisplayModifier } from 'libs/end-to-end/src/metrics';
import { MetricDisplayModifiers } from 'libs/end-to-end/src/metrics';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { BiasTaskIdWithDetails } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { defaultBiasTasksForFocusArea } from 'libs/end-to-end/src/utils/databases/default-bias-tasks-for-focus-area';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const DatabaseSchemaBiasTaskEditor = ({
  currentSchemaToSave,
  onSaved,
  show,
  setShow,
  uniqueValuesByColumn,
}: {
  currentSchemaToSave: DatabaseRevisionSchemaWithoutFilters;
  onSaved: (model: DatabaseRevisionSchemaWithoutFilters) => void;
  show: boolean;
  setShow: (show: boolean) => void;
  uniqueValuesByColumn: TransformUniqueValuesByColumnItem[];
}) => {
  const language = useLanguage();

  const metricDefinitionsWithFocusAreas = useMemo(() => {
    return currentSchemaToSave.focusAreas.flatMap((focusArea) => {
      return focusArea.metrics.map((metric) => {
        return {
          focusAreaDefinition: focusArea,
          metricDefinition: metric,
        };
      });
    });
  }, [currentSchemaToSave]);

  const metricDefinitions = useMemo(() => {
    return metricDefinitionsWithFocusAreas.map(
      (pairing) => pairing.metricDefinition,
    );
  }, [metricDefinitionsWithFocusAreas]);

  const metricsList: {
    optionLabel: string;
    optionValue: string;
  }[] = useMemo(() => {
    return metricDefinitionsWithFocusAreas.map((pairing) => {
      return {
        optionLabel: pairing.metricDefinition.displayName,
        optionValue: pairing.metricDefinition.columnHeaderValue,
      };
    });
  }, [metricDefinitionsWithFocusAreas]);

  const [currentSelectedMetricValue, setCurrentSelectedMetricValue] = useState<
    string | null
  >(null);

  const currentSelectedMetric = useMemo(() => {
    return metricDefinitionsWithFocusAreas.find(
      (pairing) =>
        pairing.metricDefinition.columnHeaderValue ===
        currentSelectedMetricValue,
    );
  }, [currentSelectedMetricValue, metricDefinitionsWithFocusAreas]);

  const biasTasksTableItems:
    | {
        taskId: BiasTaskId;
        taskDetails: BiasTaskWithoutId | null;
      }[]
    | null = useMemo(() => {
    if (!currentSelectedMetric) {
      return null;
    }

    if (!currentSelectedMetric.metricDefinition.biasTasks) {
      const defaultResult = convertDefaultBiasTasksForMetric({
        focusArea: currentSelectedMetric.focusAreaDefinition.focusArea,
        metric: currentSelectedMetric.metricDefinition,
        performanceMetric:
          currentSchemaToSave.focusAreas
            .find((fa) => fa.focusArea === 'Performance')
            ?.metrics.find((m) => m.isPreferred) ?? null,
        uniqueColumnValues: uniqueValuesByColumn,
      });

      if (defaultResult.resultType === 'err') {
        return null;
      }

      return defaultResult.value;
    } else {
      return convertBiasTasksToBiasTasksWithDetails({
        biasTasks: currentSelectedMetric.metricDefinition.biasTasks,
      });
    }
  }, [
    currentSchemaToSave.focusAreas,
    currentSelectedMetric,
    uniqueValuesByColumn,
  ]);

  const inlineMetricOptions: {
    optionLabel: string;
    optionValue: string;
  }[] = useMemo(() => {
    return metricsList.map((metric) => {
      return {
        optionLabel: metric.optionValue,
        optionValue: metric.optionValue,
      };
    });
  }, [metricsList]);

  const inlineMetricModifierOptions: ListItem<MetricDisplayModifier>[] =
    useMemo(() => {
      return MetricDisplayModifiers.map((modifier) => {
        return {
          optionLabel: modifier,
          optionValue: modifier,
        };
      });
    }, []);

  const areDefaultsEnabled = useMemo(() => {
    if (!currentSelectedMetric) {
      return false;
    }
    return !currentSelectedMetric.metricDefinition.biasTasks;
  }, [currentSelectedMetric]);

  const onAreDefaultsEnabledChanged = useCallback(
    (newValue: boolean) => {
      if (!currentSelectedMetric || !biasTasksTableItems) {
        return;
      }
      let newSchemaToSave: DatabaseRevisionSchemaWithoutFilters;
      if (newValue) {
        newSchemaToSave = changeDefaultBiasTasksForMetricOnSchema({
          metricRef: currentSelectedMetric.metricDefinition.columnHeaderValue,
          schema: currentSchemaToSave,
        });
      } else {
        newSchemaToSave = changeBiasTasksToBeCustomForMetricOnSchema({
          metricRef: currentSelectedMetric.metricDefinition.columnHeaderValue,
          schema: currentSchemaToSave,
          biasTasksToAdd: biasTasksTableItems,
        });
      }

      onSaved(newSchemaToSave);
    },
    [currentSelectedMetric, biasTasksTableItems, onSaved, currentSchemaToSave],
  );

  const onUpdateBiasTaskOnMetric = useCallback(
    ({
      taskId,
      taskDetails,
    }: {
      taskId: BiasTaskId;
      taskDetails: BiasTaskWithoutId | null;
    }) => {
      if (!currentSelectedMetric) {
        return;
      }
      const newSchema = updateBiasTaskOnMetricInSchema({
        biasTaskId: taskId,
        biasTaskDetails: taskDetails,
        metricDefinition: currentSelectedMetric.metricDefinition,
        schema: currentSchemaToSave,
      });

      onSaved(newSchema);
    },
    [currentSelectedMetric, onSaved, currentSchemaToSave],
  );

  return (
    <Modal
      show={show}
      onClose={() => setShow(false)}
      title={'Bias Task Editor'}
      scrollable={true}
    >
      <Box
        h={'h-[80vh]'}
        display={'flex'}
        flexDirection={'col'}
        overflowY={'scroll'}
      >
        <DatabaseSchemaBiasTaskEditorHeader
          areDefaultsEnabled={areDefaultsEnabled}
          onAreDefaultsEnabledChanged={onAreDefaultsEnabledChanged}
          currentMetric={currentSelectedMetricValue}
          setCurrentMetric={setCurrentSelectedMetricValue}
          metricDefinitions={metricDefinitions}
        />

        <Table
          columnDefinitions={[
            {
              id: 'id',
              header: () => <div>Bias Task</div>,
              cell: ({ item }) => (
                <S2Typography
                  variant={'body-7'}
                  translatedContent={language.biasTaskIdDisplayName({
                    biasTaskId: item.taskId,
                  })}
                />
              ),
            },
            {
              id: 'id',
              header: () => <div>Enabled</div>,
              cell: ({ item }) => (
                <Box as={'div'} display={'flex'} alignItems={'center'}>
                  {areDefaultsEnabled ? (
                    <div>{item.taskDetails !== null ? 'Yes' : 'No'}</div>
                  ) : (
                    <Switch
                      checked={item.taskDetails !== null}
                      onChange={() => {
                        const newValue = item.taskDetails === null;
                        onUpdateBiasTaskOnMetric({
                          taskId: item.taskId,
                          taskDetails:
                            newValue && item.taskDetails === null
                              ? {
                                  taskMetric: '',
                                  taskMetricDisplayName: '',
                                  isTwoSided: false,
                                  taskMetricModifier: null,
                                }
                              : null,
                        });
                      }}
                    >
                      <></>
                    </Switch>
                  )}
                </Box>
              ),
            },
            {
              id: 'taskMetric',
              header: () => <div>Task Metric</div>,
              cell: ({ item }) => (
                <Box>
                  {item.taskDetails && (
                    <Box>
                      {areDefaultsEnabled ? (
                        <div>{item.taskDetails.taskMetric}</div>
                      ) : (
                        <SelectBoxWithSearch
                          type={'single'}
                          setSelected={(selected) => {
                            if (
                              typeof selected === 'string' &&
                              item.taskDetails
                            ) {
                              onUpdateBiasTaskOnMetric({
                                taskId: item.taskId,
                                taskDetails: {
                                  ...item.taskDetails,
                                  taskMetric: selected,
                                },
                              });
                            }
                          }}
                          selected={item.taskDetails.taskMetric}
                          list={inlineMetricOptions}
                          label={null}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ),
            },
            {
              id: 'taskMetricModifier',
              header: () => <div>Task Modifier</div>,
              cell: ({ item }) => (
                <Box>
                  {item.taskDetails && item.taskDetails.taskMetricModifier && (
                    <Box>
                      {areDefaultsEnabled ? (
                        <div>{item.taskDetails.taskMetricModifier}</div>
                      ) : (
                        <SelectBoxWithSearch
                          type={'single'}
                          setSelected={(selected) => {
                            if (!item.taskDetails) {
                              return;
                            }
                            if (typeof selected === 'string') {
                              onUpdateBiasTaskOnMetric({
                                taskId: item.taskId,
                                taskDetails: {
                                  ...item.taskDetails,
                                  taskMetricModifier: selected,
                                },
                              });
                            } else if (selected === null) {
                              onUpdateBiasTaskOnMetric({
                                taskId: item.taskId,
                                taskDetails: {
                                  ...item.taskDetails,
                                  taskMetricModifier: null,
                                },
                              });
                            }
                          }}
                          selected={item.taskDetails.taskMetricModifier}
                          list={inlineMetricModifierOptions}
                          label={null}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ),
            },
            {
              id: 'taskMetricDisplayName',
              header: () => <div>Task Display</div>,
              cell: ({ item }) => (
                <Box>
                  {item.taskDetails && (
                    <Box>
                      {areDefaultsEnabled ? (
                        <div>{item.taskDetails.taskMetricDisplayName}</div>
                      ) : (
                        <input
                          className="form-input"
                          type={'text'}
                          value={item.taskDetails.taskMetricDisplayName}
                          onChange={(e) => {
                            if (!item.taskDetails) {
                              return;
                            }

                            onUpdateBiasTaskOnMetric({
                              taskId: item.taskId,
                              taskDetails: {
                                ...item.taskDetails,
                                taskMetricDisplayName: e.target.value,
                              },
                            });
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ),
            },
            {
              id: 'isTwoSided',
              header: () => <div>Is Two Sided</div>,
              cell: ({ item }) => (
                <Box as={'div'} display={'flex'} alignItems={'center'}>
                  {item.taskDetails && (
                    <Box>
                      {areDefaultsEnabled ? (
                        <div>{item.taskDetails.isTwoSided ? 'Yes' : 'No'}</div>
                      ) : (
                        <Switch
                          checked={item.taskDetails.isTwoSided}
                          onChange={() => {
                            if (!item.taskDetails) {
                              return;
                            }

                            onUpdateBiasTaskOnMetric({
                              taskId: item.taskId,
                              taskDetails: {
                                ...item.taskDetails,
                                isTwoSided: !item.taskDetails.isTwoSided,
                              },
                            });
                          }}
                        >
                          <></>
                        </Switch>
                      )}
                    </Box>
                  )}
                </Box>
              ),
            },
          ]}
          items={biasTasksTableItems ?? []}
        />
      </Box>
    </Modal>
  );
};

export const convertDefaultBiasTasksForMetric = ({
  focusArea,
  metric,
  performanceMetric,
  uniqueColumnValues,
}: {
  focusArea: FocusArea;
  metric: MetricDefinition;
  performanceMetric: MetricDefinition | null;
  uniqueColumnValues: TransformUniqueValuesByColumnItem[];
}): Result<BiasTaskIdWithDetails[], BaseError> => {
  const tasksRecordResult = defaultBiasTasksForFocusArea({
    focusArea,
    metric,
    performanceMetric,
    uniqueColumnValues,
  });

  if (tasksRecordResult.resultType === 'err') {
    return tasksRecordResult;
  }

  const biasTasks: BiasTaskIdWithDetails[] = [];

  for (const [key, value] of Object.entries(tasksRecordResult.value)) {
    biasTasks.push({
      taskId: key as BiasTaskId,
      taskDetails: value,
    });
  }

  return okResult(biasTasks);
};

export const changeDefaultBiasTasksForMetricOnSchema = ({
  metricRef,
  schema,
}: {
  metricRef: string;
  schema: DatabaseRevisionSchemaWithoutFilters;
}): DatabaseRevisionSchemaWithoutFilters => {
  const newFocusAreaDefinitions = schema.focusAreas.map((focusArea) => {
    return {
      ...focusArea,
      metrics: focusArea.metrics.map((metric) => {
        if (metric.columnHeaderValue === metricRef) {
          return {
            ...metric,
            biasTasks: null,
          };
        } else {
          return metric;
        }
      }),
    };
  });

  return {
    ...schema,
    focusAreas: newFocusAreaDefinitions,
  };
};

export const changeBiasTasksToBeCustomForMetricOnSchema = ({
  metricRef,
  schema,
  biasTasksToAdd,
}: {
  metricRef: string;
  schema: DatabaseRevisionSchemaWithoutFilters;
  biasTasksToAdd: BiasTaskIdWithDetails[];
}): DatabaseRevisionSchemaWithoutFilters => {
  const newFocusAreaDefinitions = schema.focusAreas.map((focusArea) => {
    return {
      ...focusArea,
      metrics: focusArea.metrics.map((metric) => {
        if (metric.columnHeaderValue === metricRef) {
          const newBiasTasks: BiasTask[] = [];

          for (const task of biasTasksToAdd) {
            if (task.taskDetails) {
              newBiasTasks.push({
                taskId: task.taskId,
                taskMetric: task.taskDetails.taskMetric,
                taskMetricModifier: task.taskDetails.taskMetricModifier,
                taskMetricDisplayName: task.taskDetails.taskMetricDisplayName,
                isTwoSided: task.taskDetails.isTwoSided,
              });
            }
          }
          return {
            ...metric,
            biasTasks: newBiasTasks,
          };
        } else {
          return metric;
        }
      }),
    };
  });

  return {
    ...schema,
    focusAreas: newFocusAreaDefinitions,
  };
};

export const convertBiasTasksToBiasTasksWithDetails = ({
  biasTasks,
}: {
  biasTasks: BiasTask[];
}): BiasTaskIdWithDetails[] => {
  const tasksConfigured: {
    taskId: BiasTaskId;
    taskDetails: BiasTaskWithoutId | null;
  }[] = biasTasks.map((task) => {
    return {
      taskId: task.taskId,
      taskDetails: task,
    };
  });

  for (const taskId of BiasTaskIds) {
    if (!tasksConfigured.find((task) => task.taskId === taskId)) {
      tasksConfigured.push({
        taskId,
        taskDetails: null,
      });
    }
  }

  return tasksConfigured;
};

export const DatabaseSchemaBiasTaskEditorHeader = ({
  metricDefinitions,
  currentMetric,
  setCurrentMetric,
  areDefaultsEnabled,
  onAreDefaultsEnabledChanged,
}: {
  metricDefinitions: MetricDefinition[];
  currentMetric: string | null;
  setCurrentMetric: (metric: string) => void;
  areDefaultsEnabled: boolean;
  onAreDefaultsEnabledChanged: (areDefaultsEnabled: boolean) => void;
}) => {
  const metricsList: {
    optionLabel: string;
    optionValue: string;
  }[] = useMemo(() => {
    return metricDefinitions.map((metric) => {
      return {
        optionLabel: metric.displayName,
        optionValue: metric.columnHeaderValue,
      };
    });
  }, [metricDefinitions]);

  return (
    <Box display={'flex'} flexDirection={'col'}>
      <Spacing direction={'vertical'} size={'md'} as={'div'}>
        <SelectBoxWithSearch
          type={'single'}
          setSelected={(selected) => {
            setCurrentMetric(selected);
          }}
          selected={currentMetric ?? ''}
          list={metricsList}
          label={null}
        />
      </Spacing>

      <Box as={'div'} alignSelf={'start'}>
        <S2Checkbox
          checked={areDefaultsEnabled}
          onChange={() => {
            onAreDefaultsEnabledChanged(!areDefaultsEnabled);
          }}
        >
          <div>Are Defaults Enabled?</div>
        </S2Checkbox>
      </Box>
    </Box>
  );
};

export const updateBiasTaskOnMetricInSchema = ({
  biasTaskId,
  biasTaskDetails,
  metricDefinition,
  schema,
}: {
  biasTaskId: BiasTaskId;
  biasTaskDetails: BiasTaskWithoutId | null;
  metricDefinition: MetricDefinition;
  schema: DatabaseRevisionSchemaWithoutFilters;
}): DatabaseRevisionSchemaWithoutFilters => {
  const biasTasks = metricDefinition.biasTasks;

  if (!biasTasks) {
    return schema;
  }

  // find index where bias task id exists
  const index = biasTasks.findIndex((task) => task.taskId === biasTaskId);

  if (!biasTaskDetails) {
    // delete from array
    if (index !== -1) {
      biasTasks.splice(index, 1);
    }
  } else {
    // if index is found, update the bias task
    if (index !== -1) {
      biasTasks[index] = {
        taskId: biasTaskId,
        ...biasTaskDetails,
      };
    } else {
      // if index is not found, add the bias task
      biasTasks.push({
        taskId: biasTaskId,
        ...biasTaskDetails,
      });
    }
  }

  const newFocusAreaDefinitions = schema.focusAreas.map((focusArea) => {
    return {
      ...focusArea,
      metrics: focusArea.metrics.map((metric) => {
        if (metric.columnHeaderValue === metricDefinition.columnHeaderValue) {
          return {
            ...metric,
            biasTasks: biasTasks,
          };
        } else {
          return metric;
        }
      }),
    };
  });

  return {
    ...schema,
    focusAreas: newFocusAreaDefinitions,
  };
};
